<template>
  <div v-cloak>
    <transition appear appear-active-class="fade-enter-active" name="fade">
      <div v-if="apiLoaded || errorText">
        <div v-if="apiLoaded" class="wrap">
          <h1 v-if="id != null && id.length > 0">
            Редактирование пользователя
          </h1>
          <h1 v-else>Создание пользователя</h1>

          <div class="roles-block">
            <div class="roles-block__title">
              Роли
              <v-icon
                class="icon-item icon-information-outline-black roles-block__info"
                title="Описание ролей"
                @click="rolesInfoDialog = true"
              ></v-icon>
            </div>

            <v-select
              class="roles-block__field"
              label="Основная роль"
              v-model="user.primaryRole"
              :items="roleOptions"
              item-text="text"
              item-value="type"
              @change="onPrimaryRoleChange($event)"
            ></v-select>

            <v-select
              v-if="canSeeAvailableRoles"
              class="roles-block__field"
              multiple
              small-chips
              label="Доступные основные роли"
              v-model="user.availablePrimaryRoles"
              :items="availableRoleOptions"
              :menu-props="{ contentClass: 'roles-menu' }"
              item-text="text"
              item-value="type"
              @change="onSelectChange($event)"
            ></v-select>

            <v-select
              v-if="hasRegionalRole && canSeeSecondaryRoles"
              class="roles-block__field"
              multiple
              small-chips
              label="Второстепенные роли"
              :items="secondaryRoleOptions"
              item-text="description"
              item-value="role"
              v-model="user.secondaryRoles"
            ></v-select>

            <div class="domain-roles">
              <div class="domain-roles__title">Доменные роли</div>

              <div v-if="!isExistingUser" class="domain-roles__empty">
                Назначить доменные роли можно после создания пользователя
              </div>

              <div v-else-if="domainRoles.length === 0" class="domain-roles__empty">Роли не назначены</div>

              <div v-for="assigned in domainRoles" :key="assigned.domainRole.key" class="domain-roles__item">
                <div class="domain-roles__info">
                  <div class="domain-roles__domain">{{ domainTitle(assigned.domainRole) }}</div>
                  <div class="domain-roles__name">
                    <span>{{ assigned.domainRole.title }}</span>
                    <v-chip
                      v-for="role in allowedRoles(assigned.domainRole)"
                      :key="role.value"
                      x-small
                      outlined
                      label
                      class="domain-roles__role-chip"
                    >{{ role.text }}</v-chip>
                  </div>
                  <div class="domain-roles__description">{{ assigned.domainRole.description }}</div>
                  <div v-if="assigned.scopeIds.length" class="domain-roles__scope">
                    <div
                      v-for="group in scopeGroups(assigned)"
                      :key="group.title || 'raw'"
                      class="domain-roles__scope-group"
                    >
                      <div v-if="group.title" class="domain-roles__scope-group-title">{{ group.title }}</div>
                      <div v-for="title in group.items" :key="title">{{ title }}</div>
                    </div>
                  </div>
                </div>
                <v-icon
                  class="icon-item icon-delete-outline domain-roles__remove"
                  title="Снять роль"
                  @click="removeDomainRole(assigned.domainRole.key)"
                ></v-icon>
              </div>

              <div v-if="isExistingUser && primaryRolesChanged" class="domain-roles__empty">
                Назначить доменные роли можно после сохранения изменений основных ролей
              </div>

              <div v-if="isExistingUser" class="domain-roles__add">
                <v-select
                  label="Добавить роль"
                  v-model="selectedDomainRoleKey"
                  :items="domainRoleOptions"
                  :menu-props="{ contentClass: 'roles-menu domain-roles__menu' }"
                  item-text="title"
                  item-value="key"
                  hide-details
                  clearable
                  @change="onDomainRoleSelected"
                >
                  <template v-slot:item="{ item, on, attrs }">
                    <v-list-item v-bind="attrs" v-on="on">
                      <v-list-item-content>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                        <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                        <v-list-item-subtitle class="domain-roles__allowed">
                          {{ allowedRolesText(item) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
                <v-btn
                  color="primary"
                  outlined
                  :disabled="!selectedDomainRoleKey || primaryRolesChanged"
                  @click="assignDomainRole"
                >Добавить</v-btn>
              </div>

              <div v-if="isExistingUser && needsScope" class="domain-roles__scope-picker">
                <div class="field-hint">
                  Выберите области ответственности
                </div>
                <v-treeview
                  v-if="scopeTree.length"
                  v-model="selectedScopeIds"
                  :items="scopeTree"
                  selectable
                  dense
                  item-key="id"
                  item-text="title"
                >
                  <template v-slot:label="{ item }">
                    <span :title="item.title">{{ item.title }}</span>
                  </template>
                </v-treeview>
                <div v-else class="domain-roles__empty">
                  Не удалось загрузить структуру показателей домена
                </div>
              </div>
            </div>
          </div>

          <v-row>
            <v-text-field
              label="Имя пользователя (username)"
              :readonly="id != null"
              v-model="user.username"
            ></v-text-field>
          </v-row>

          <v-row>
            <v-text-field label="Email" v-model="user.email"></v-text-field>
          </v-row>

          <v-row>
            <v-text-field label="Полное имя" v-model="user.name"></v-text-field>
          </v-row>

          <v-row v-if="needsRegion">
            <v-autocomplete
              label="Регион"
              v-model="user.region"
              :items="regionsList"
              item-text="name"
              return-object
              style="width: 40%"
              @change="onRegionChange($event)"
              :readonly="$user.role === 'ROLE_REGIONAL_ADMINISTRATOR'"
              :error-messages="decentralizedRegionError"
            ></v-autocomplete>
          </v-row>

          <v-row v-if="needsDepartment">
            <v-autocomplete
              v-if="searchType === 'department'"
              :items="departments"
              v-model="user.department"
              item-text="name"
              return-object
              label="Название ЦЗН"
              style="width: 40%"
            ></v-autocomplete>
            <EgrulInput
              v-else-if="searchType === 'egrul'"
              @changeNameOfUser="changeNameOfUser"
              :init-value="user.department"
            ></EgrulInput>
          </v-row>

          <v-row v-if="needsDepartment && searchType === 'egrul'">
            <v-checkbox
              label="Управляющий ЦЗН"
              v-model="isManager"
            ></v-checkbox>
          </v-row>

          <v-row>
            <v-checkbox
              label="Активная учетная запись"
              v-model="user.enabled"
            ></v-checkbox>
          </v-row>

          <v-row>
            <v-checkbox
              label="Не направлять электронные письма"
              v-model="user.stopEmails"
            ></v-checkbox>
          </v-row>

          <v-row v-if="id == null">
            <v-checkbox
              label="Сгенерировать пароль"
              v-model="needGeneratedPassword"
              @change="user.password = null"
            ></v-checkbox>
          </v-row>

          <v-row>
            <v-text-field v-if="id == null && needGeneratedPassword" label="Пароль" disabled></v-text-field>
            <v-text-field v-else-if="id == null" label="Пароль" v-model="user.password"></v-text-field>
            <v-text-field v-else label="Новый пароль" v-model="user.password"></v-text-field>
          </v-row>

          <v-dialog v-model="rolesInfoDialog" max-width="720">
            <v-card>
              <v-card-title>О ролях</v-card-title>
              <v-card-text>
                <div class="role-info__section">Основная роль</div>
                <div class="role-info__description">
                  Определяет права пользователя в системе. У пользователя одна основная роль.
                </div>

                <v-expansion-panels accordion flat class="role-info__panels">
                  <v-expansion-panel>
                    <v-expansion-panel-header>Описание основных ролей</v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <div v-for="role in roleInfoList" :key="role.type" class="role-info">
                        <div class="role-info__title">{{ role.text }}</div>
                        <div class="role-info__description">{{ role.description }}</div>
                      </div>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>

                <template v-if="canSeeAvailableRoles">
                  <div class="role-info__section">Доступные основные роли</div>
                  <div class="role-info__description">
                    Роли, на которые пользователь может переключиться сам через меню профиля в рамках своей учетной записи.
                    <br>
                    Регион и ЦЗН у всех ролей общие, поэтому УЦ и КС совместить нельзя.
                  </div>
                </template>

                <template v-if="canSeeSecondaryRoles">
                  <div class="role-info__section">Второстепенные роли</div>
                  <div class="role-info__description">
                    Устаревший механизм дополнительных прав. Поле оставлено только для уже выданных прав.
                  </div>
                </template>

                <div class="role-info__section">Доменные роли</div>
                <div class="role-info__description">
                  Отвечают за доступ внутри отдельных модулей - например, за право верифицировать определенные
                  показатели мониторинга.
                </div>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="primary" text @click="rolesInfoDialog = false">Закрыть</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <div class="save-buttons mt-24">
            <v-btn
              color="primary"
              outlined
              @click="resetPassword"
              >Сбросить пароль</v-btn
            >
            <v-btn
              color="primary"
              outlined
              @click="$router.push('/config/users')"
              >Отмена</v-btn
            >
            <v-btn
              :disabled="submitButtonDisabled"
              color="primary"
              @click="submit"
              >Сохранить</v-btn
            >
          </div>

          <div style="float: right" v-if="$user.role !== 'ROLE_REGIONAL_ADMINISTRATOR'">
            <a
              href="#"
              @click.prevent="deleteUser"
              v-if="
                id != null &&
                id.length > 0
              "
              >Удалить пользователя</a
            >
          </div>
        </div>

        <div
          v-if="errorText != null"
          class="error"
          style="position: fixed; bottom: 0"
        >
          Ошибка: {{ errorText }}
        </div>
      </div>
      <div v-else style="height: 1000px"></div>
    </transition>
  </div>
</template>

<style lang="scss">
@import "../../styles/main.scss";
.field-hint {
  margin: 8px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: $cgray;
}

.roles-block {
  margin: 8px 0 32px;
  padding: 4px 0 16px 24px;
  border-left: 3px solid $cblue2;

  .v-input {
    margin-top: 0;
    padding-top: 0;
  }
}

.roles-block__field {
  margin-top: 20px;
}

.roles-block__title {
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 18px;
  line-height: 22px;
  color: $cblack;
}

.roles-block__info {
  margin-left: 6px;
  cursor: pointer;
  opacity: 0.55;

  &:hover {
    opacity: 1;
  }
}

.role-info__section {
  margin: 28px 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid $lgray;
  font-weight: 600;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: $cblue;

  &:first-child {
    margin-top: 0;
  }
}

.role-info {
  padding: 12px 0;
  border-top: 1px solid $lgray;

  &:first-child {
    border-top: none;
    padding-top: 0;
  }
}

.role-info__title {
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: $cblack;
}

.role-info__panels {
  margin-top: 4px;

  .v-expansion-panel-header {
    justify-content: flex-start;
    min-height: 32px;
    padding: 4px 0;
    font-size: 13px;
    line-height: 18px;
    color: $cblue;
    text-decoration: underline;

    &:hover {
      opacity: 0.75;
    }
  }

  .v-expansion-panel-header__icon {
    margin-left: 6px;

    .v-icon {
      color: $cblue;
    }
  }

  .v-expansion-panel-content__wrap {
    padding: 0 0 12px;
  }
}

.role-info__description {
  margin-top: 4px;
  font-size: 13px;
  line-height: 18px;
  color: $cgray;
}

.domain-roles {
  margin-top: 12px;
}

.domain-roles__title {
  font-weight: 600;
  font-size: 16px;
  line-height: 20px;
  color: $cblack;
}

.domain-roles__empty {
  margin-top: 16px;
  font-size: 13px;
  color: $cgray;
}

.domain-roles__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid $lgray;
}

.domain-roles__info {
  flex: 1 1 auto;
  min-width: 0;
}

.domain-roles__domain {
  margin-bottom: 2px;
  font-size: 11px;
  line-height: 14px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: $cblue;
}

.domain-roles__name {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 18px;
  color: $cblack;
}

.domain-roles__role-chip {
  font-size: 11px;
}

.domain-roles__scope {
  margin-top: 6px;
  padding-left: 12px;
  border-left: 2px solid $lgray;
  font-size: 12px;
  line-height: 17px;
  color: $cblack;
}

.domain-roles__scope-group + .domain-roles__scope-group {
  margin-top: 8px;
}

.domain-roles__scope-group-title {
  margin-bottom: 2px;
  font-weight: 600;
  color: $cgray;
}

.domain-roles__scope-picker {
  margin-top: 12px;
}

.domain-roles__allowed {
  margin-top: 4px;
  font-size: 11px;
  line-height: 15px;
  color: rgba(0, 0, 0, 0.38);
}

.domain-roles__description {
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: $cgray;
}

.domain-roles__remove {
  flex: 0 0 24px;
  cursor: pointer;
  opacity: 0.55;

  &:hover {
    opacity: 1;
  }
}

.domain-roles__add {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 20px;

  .v-input {
    flex: 0 1 420px;
  }
}

.domain-roles__menu {
  .v-list-item__subtitle {
    margin-top: 2px;
    font-size: 12px;
    line-height: 16px;
    white-space: normal;
  }

  .v-list-item {
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .v-subheader {
    height: 32px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $cblue;
  }
}

.roles-menu {
  .v-list-item--disabled {
    background: rgba(0, 0, 0, 0.04);
    cursor: not-allowed;

    .v-list-item__title,
    .v-simple-checkbox .v-icon {
      color: rgba(0, 0, 0, 0.38) !important;
    }
  }
}
</style>

<style lang="scss" scoped>
</style>

<script>
import EgrulInput from "../elements/EgrulInput";
import api from "@/modules/api";
import {getActiveDomainConfigs} from "@/modules/api.configs";

/**
 * Вложенность конфига домена. Последний уровень попадает в скоуп. Верхние - для группировки
 */
const SCOPE_STRUCTURE = {
  org_monitor: [
    {items: 'sections', id: 'id', title: 'name'},
    {items: 'indicators', id: 'id', title: 'name'},
  ],
};

import {PRIMARY_ROLES, REGIONAL_ADMIN_MANAGED_ROLES, ROLE_LABELS} from "@/modules/constants";
export default {
  props: {},
  components: { EgrulInput },
  data() {
    return {
      apiLoaded: false,
      regionsList: [],
      id: null,
      type: null,
      submitButtonDisabled: false,
      initialEmail: null,
      previousPrimaryRole: null,
      rolesInfoDialog: false,
      domainRoles: [],
      assignableDomainRoles: [],
      selectedDomainRoleKey: null,
      selectedScopeIds: [],
      scopeTreeByDomain: {},
      savedPrimaryRoles: [],
      user: {
        primaryRole: 'ROLE_CZN_TERRITORIAL',
        username: null,
        name: null,
        email: null,
        stopEmails: false,
        region: null,
        enabled: true,
        department: null,
        secondaryRoles: [],
        availablePrimaryRoles: [],
      },
      roleDescriptions: {
        ROLE_ADMIN: "Администраторам доступен полный просмотр информации в системе, настройка подсистем, управление пользователями всех регионов, экспорт данных, массовая рассылка уведомлений. Могут администрировать роли и структурные подразделения. Не редактируют сведения, заполняемые кадровыми центрами.",
        ROLE_VNII: "Методическое сопровождение системы, консультационная поддержка пользователей. Просмотр сведений по всем регионам, проверка и верификация данных, формирование рейтингов и аналитических справок. Формирование мастер-данных для других подсистем. Экспорт данных.",
        ROLE_MINTRUD: "Просмотр сведений по всем субъектам РФ. Контроль деятельности службы занятости в регионах. Формирование и просмотр рейтингов, анализ исполнения стандартов. Экспорт отчётов и аналитических материалов.",
        ROLE_REGIONAL_ADMINISTRATOR: "Управление пользователями и структурными подразделениями своего субъекта РФ: создание, редактирование, восстановление доступа, ведение справочника организационной структуры. Координация работы по технической поддержке на региональном уровне, информирование пользователей о порядке работы в системе.",
        ROLE_REGIONAL_AGENCY: "Оперативный контроль деятельности клиентских служб региона. Заполнение показателей, верификация сведений, формирование региональных и локальных отчётов. Просмотр паспортов всех кадровых центров субъекта, работа с анкетами и опросами. Участие в формировании рейтингов.",
        ROLE_CZN_MANAGER: "Заполнение сведений по управляющему центру. Контроль, редактирование и верификация данных территориальных клиентских служб. Формирование региональных и локальных отчётов. Просмотр паспортов, работа с анкетами и опросами.",
        ROLE_CZN_TERRITORIAL: "Заполнение и обновление сведений по своей клиентской службе. Просмотр паспорта своего подразделения. Работа с анкетами и опросами, распространение ссылок среди клиентов. Формирование локальных отчётов.",
      },
      roleOptions: PRIMARY_ROLES.map(type => ({text: ROLE_LABELS[type], type})),
      errorText: null,
      secondaryRoleOptions: [],
      searchType: "department",
      departments: [],
      isManager: null,
      needGeneratedPassword: false,
    };
  },
  computed: {
    allPrimaryRoles() {
      const roles = [this.user.primaryRole].concat(this.user.availablePrimaryRoles || []);
      return roles.filter((role, i) => role != null && roles.indexOf(role) === i);
    },
    selectedCznRole() {
      return this.allPrimaryRoles.find(
          role => role === 'ROLE_CZN_MANAGER' || role === 'ROLE_CZN_TERRITORIAL') || null;
    },
    conflictingRole() {
      if (this.selectedCznRole === 'ROLE_CZN_TERRITORIAL') return 'ROLE_CZN_MANAGER';
      if (this.selectedCznRole === 'ROLE_CZN_MANAGER') return 'ROLE_CZN_TERRITORIAL';
      return null;
    },
    selectedDomainRole() {
      return this.assignableDomainRoles.find(role => role.key === this.selectedDomainRoleKey);
    },
    needsScope() {
      return this.selectedDomainRole && this.selectedDomainRole.accessMode === 'BY_SCOPE';
    },
    scopeTree() {
      const domainCode = this.selectedDomainRole ? this.selectedDomainRole.domainCode : null;
      return this.scopeTreeByDomain[domainCode] || [];
    },
    isExistingUser() {
      return this.id != null && this.id.length > 0;
    },
    primaryRolesChanged() {
      const current = [...this.allPrimaryRoles].sort().join(',');
      const saved = [...(this.savedPrimaryRoles || [])].sort().join(',');
      return current !== saved;
    },
    domainRoleOptions() {
      const assignedKeys = this.domainRoles.map(assigned => assigned.domainRole.key);
      const roles = this.assignableDomainRoles
          .filter(role => !assignedKeys.includes(role.key))
          .map(role => ({...role, disabled: this.primaryRolesChanged || !this.canAssignRole(role)}))
          .sort((first, second) => this.domainTitle(first).localeCompare(this.domainTitle(second))
              || first.title.localeCompare(second.title));

      const options = [];
      let currentDomain = null;
      roles.forEach(role => {
        if (this.domainTitle(role) !== currentDomain) {
          currentDomain = this.domainTitle(role);
          options.push({header: currentDomain});
        }
        options.push(role);
      });
      return options;
    },
    roleInfoList() {
      return this.roleOptions
          .filter(option => this.roleDescriptions[option.type])
          .map(option => ({...option, description: this.roleDescriptions[option.type]}));
    },
    availableRoleOptions() {
      return this.roleOptions.map(option => ({
        ...option,
        disabled: option.type === this.user.primaryRole || option.type === this.conflictingRole,
      }));
    },
    isDecentralizedRegion() {
      return this.user.region && this.user.region.netType === 'DECENTRALIZED';
    },
    decentralizedRegionError() {
      return this.hasManagerCznRole && this.isDecentralizedRegion
          ? 'В децентрализованном регионе роль «Управляющий центр» недоступна'
          : [];
    },
    needsDepartment() {
      return this.allPrimaryRoles.some(
          role => role === 'ROLE_CZN_MANAGER' || role === 'ROLE_CZN_TERRITORIAL');
    },
    hasManagerCznRole() {
      return this.allPrimaryRoles.includes('ROLE_CZN_MANAGER');
    },
    hasRegionalRole() {
      return this.allPrimaryRoles.some(
          role => role === 'ROLE_REGIONAL_AGENCY' || role === 'ROLE_REGIONAL_ADMINISTRATOR');
    },
    canSeeSecondaryRoles() {
      return this.$user.role === 'ROLE_ADMIN';
    },
    canSeeAvailableRoles() {
      return this.$user.role === 'ROLE_ADMIN';
    },
    needsRegion() {
      return this.needsDepartment || this.hasRegionalRole;
    },
  },
  methods: {
    async loadUserInfo() {
      let req = await this.$getApi("/getUserInfo", {
        type: this.type,
        id: this.id,
      });
      if (req.ok) {
        this.user = req.payload;
        this.initialEmail = this.user.email;
        this.snapshotPrimaryRoles();
        this.previousPrimaryRole = this.user.primaryRole;
        this.ensurePrimaryRoleAvailable();
      } else {
        this.errorText =
            "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
    },

    async loadRegionsList() {
      let req = await this.$getApi("/getRegionsList");
      if (req.ok) {
        this.regionsList = req.payload;
      } else {
        this.errorText =
          "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
    },

    onPrimaryRoleChange(newRole) {
      if (this.previousPrimaryRole && this.previousPrimaryRole !== newRole) {
        this.user.availablePrimaryRoles = (this.user.availablePrimaryRoles || []).filter(role => role !== this.previousPrimaryRole);
      }
      this.previousPrimaryRole = newRole;
      this.assignDefaultManagerCzn();
      this.onSelectChange();
    },

    onRegionChange() {
      this.assignDefaultManagerCzn();
      this.onSelectChange();
    },

    onSelectChange() {
      this.ensurePrimaryRoleAvailable();
    },

    assignDefaultManagerCzn() {
      if (!this.hasManagerCznRole || !this.user.region || this.isDecentralizedRegion) {
        return;
      }
      const regionCode = this.user.region.code;
      this.user.department = this.departments.find(department =>
          department.type === 'manager_czn' && department.region && department.region.code === regionCode) || null;
    },

    ensurePrimaryRoleAvailable() {
      const order = this.roleOptions.map(option => option.type);
      const rank = role => {
        const index = order.indexOf(role);
        return index === -1 ? order.length : index;
      };

      const available = (this.user.availablePrimaryRoles || [])
          .filter(role => role !== this.conflictingRole);
      if (this.user.primaryRole && !available.includes(this.user.primaryRole)) {
        available.push(this.user.primaryRole);
      }
      available.sort((a, b) => rank(a) - rank(b));

      this.$set(this.user, 'availablePrimaryRoles', available);
    },

    changeNameOfUser(val) {
      this.user.department = val;
    },

    validate() {
      if (this.user.username == null || this.user.username.length === 0) {
        return "Укажите имя пользователя (username)";
      }
      if (this.user.name == null || this.user.name.length === 0) {
        return "Укажите полное имя пользователя";
      }

      if (this.hasManagerCznRole && this.isDecentralizedRegion) {
        return "В децентрализованном регионе роль «Управляющий центр» недоступна";
      }

      if (this.needsDepartment) {
        if (this.user.department == null || this.user.department.length === 0) {
          return "Выберите организацию (ЦЗН)";
        }
        if (this.user.department.type == null) {
          return "У выбранной организации не указан тип (территориальный или управляющий ЦЗН / ОИВ). Перед привязыванием к нему пользователей, пожалуйста, укажите данную информацию в справочнике ЦЗН и ОИВ";
        }
        if (this.allPrimaryRoles.includes('ROLE_CZN_MANAGER') && this.user.department.type !== "manager_czn") {
          return "Роль пользователя (Управляющий центр) не соответствует типу выбранного ЦЗН"
        }
        if (this.allPrimaryRoles.includes('ROLE_CZN_TERRITORIAL') && this.user.department.type !== "territorial_czn") {
          return "Роль пользователя (Клиентская служба) не соответствует типу выбранного ЦЗН"
        }
      }

      const orphaned = this.domainRoles
          .map(assigned => assigned.domainRole)
          .filter(role => !this.canAssignRole(role));
      if (orphaned.length > 0) {
        const role = orphaned[0];
        return 'Доменная роль «' + role.title + '» ' + this.allowedRolesText(role)
            + '. Снимите её или верните пользователю одну из этих ролей';
      }

      if (this.isExistingUser && this.selectedDomainRoleKey) {
        const title = this.selectedDomainRole ? this.selectedDomainRole.title : '';
        return 'Доменная роль «' + title + '» выбрана, но не добавлена. Нажмите «Добавить» или снимите выбор.';
      }

      if (this.user.email == null || this.user.email.length === 0) {
        return "Укажите адрес электронной почты";
      }

      if (!/.+@.+\..+/.test(this.user.email)) {
        return "Введите корректный адрес электронной почты";
      }

      if (this.needsRegion
          && (this.user.region == null || this.user.region.length === 0)) {
        return "Выберите регион"
      }

      return true;
    },

    async checkUsernameExists() {
      let req = await this.$getApi("/userNameExists", {
        username: this.user.username,
      });
      if (req.ok) {
        if (req.payload === true) return true;
        return false;
      }
    },

    async checkEmailExists() {
      let req = await this.$getApi("/checkEmailExists", {
        email: this.user.email,
      });
      if (req.ok) {
        if (req.payload === true) return true;
        return false;
      }
      return false;
    },

    async resetPassword() {
      let req = await this.$postApi("/resetUserPassword", this.user);
      if (req.ok) {
        alert('Пароль сброшен. На почту пользователя направлен новый пароль.')
      } else {
        this.errorText =
            "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
    },

    async submit() {
      this.submitButtonDisabled = true;
      let t = this.validate();
      if (t !== true) {
        this.errorText = t;
        this.submitButtonDisabled = false;
        return;
      }
      this.errorText = null;

      let userExists = await this.checkUsernameExists();
      if (this.user.id == null && userExists === true) {
        alert("Пользователь с таким именем уже существует");
        this.submitButtonDisabled = false;
        return;
      }

      if (this.user.email !== this.initialEmail) {
        let emailExists = await this.checkEmailExists();
        if (emailExists) {
          alert("Пользователь с таким адресом электронной почты уже существует");
          this.submitButtonDisabled = false;
          return;
        }
      }

      if (this.searchType === 'egrul') {
        if (this.isManager) {
          this.user.department.type = 'manager_czn';
        } else {
          this.user.department.type = 'territorial_czn';
        }
      }

      if (this.user.allRoles) {
        delete this.user['allRoles'];
      }

      if (!this.needsDepartment){
        this.user.department = null;
      }

      if (!this.hasRegionalRole){
        this.user.secondaryRoles = null
      }

      let req = await this.$postApi("/saveUser", this.user);
      if (req.ok) {
        this.$router.push("/config/users");
      } else {
        this.errorText =
          "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
      this.submitButtonDisabled = false;
    },

    async deleteUser() {
      if (!confirm("Вы действительно хотите удалить пользователя?")) return;

      let req = await api.delete("/deleteUser", {
        id: this.user.id,
      });
      if (req.ok) {
        this.$router.push("/config/users");
      } else {
        this.errorText =
          "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
    },

    async onDomainRoleSelected() {
      this.selectedScopeIds = [];
      if (this.needsScope) {
        await this.loadScopeTree(this.selectedDomainRole.domainCode);
      }
    },

    async loadScopeTree(domainCode) {
      if (this.scopeTreeByDomain[domainCode]) {
        return;
      }
      const structure = SCOPE_STRUCTURE[domainCode];
      const req = await getActiveDomainConfigs(domainCode);
      const content = req.ok ? req.payload?.[0]?.content : null;
      this.$set(this.scopeTreeByDomain, domainCode,
          structure && content ? this.buildScopeTree(content, structure, 0) : []);
    },

    buildScopeTree(parent, structure, levelIndex, parentNumber = '') {
      const level = structure[levelIndex];
      const isLast = levelIndex === structure.length - 1;
      return (parent[level.items] || []).map((item, index) => {
        const number = parentNumber + (index + 1) + '.';
        const node = {
          id: isLast ? String(item[level.id]) : level.items + ':' + item[level.id],
          title: number + ' ' + item[level.title],
        };
        if (!isLast) {
          node.children = this.buildScopeTree(item, structure, levelIndex + 1, number);
        }
        return node;
      });
    },

    scopeGroups(assigned) {
      const selected = new Set(assigned.scopeIds);
      const groups = [];

      const walk = (nodes, groupTitle) => {
        nodes.forEach(node => {
          if (node.children) {
            walk(node.children, node.title);
          } else if (selected.has(node.id)) {
            let group = groups.find(item => item.title === groupTitle);
            if (!group) {
              group = {title: groupTitle, items: []};
              groups.push(group);
            }
            group.items.push(node.title);
          }
        });
      };
      walk(this.scopeTreeByDomain[assigned.domainRole.domainCode] || [], null);

      return groups.length ? groups : [{title: null, items: [...assigned.scopeIds]}];
    },

    domainTitle(role) {
      return role.domainTitle || role.domainCode;
    },

    canAssignRole(role) {
      return !role.allowedPrimaryRoles
          || role.allowedPrimaryRoles.length === 0
          || role.allowedPrimaryRoles.some(allowed => this.allPrimaryRoles.includes(allowed));
    },

    allowedRoles(role) {
      if (!role.allowedPrimaryRoles || role.allowedPrimaryRoles.length === 0) {
        return [{value: 'ANY', text: 'Любая роль'}];
      }
      return role.allowedPrimaryRoles.map(item => ({value: item, text: ROLE_LABELS[item] || item}));
    },

    allowedRolesText(role) {
      if (!role.allowedPrimaryRoles || role.allowedPrimaryRoles.length === 0) {
        return 'Доступна любой основной роли';
      }
      return 'Доступно для ролей: '
          + role.allowedPrimaryRoles.map(item => ROLE_LABELS[item] || item).join(', ');
    },

    async loadDomainRoles() {
      const assigned = await this.$getApi('/domain-roles/assigned', {userId: this.id});
      if (assigned.ok) {
        this.domainRoles = assigned.payload;
      }
      const assignable = await this.$getApi('/domain-roles');
      if (assignable.ok) {
        this.assignableDomainRoles = assignable.payload;
      }

      const domainsWithScope = this.domainRoles
          .filter(assigned => assigned.scopeIds.length)
          .map(assigned => assigned.domainRole.domainCode);
      await Promise.all([...new Set(domainsWithScope)].map(domain => this.loadScopeTree(domain)));
    },

    snapshotPrimaryRoles() {
      this.savedPrimaryRoles = this.allPrimaryRoles.slice();
    },

    async assignDomainRole() {
      if (this.primaryRolesChanged) {
        this.errorText = 'Сначала сохраните изменения основных ролей, затем назначайте доменную роль';
        return;
      }
      if (this.selectedDomainRole && !this.canAssignRole(this.selectedDomainRole)) {
        this.errorText = 'Доменная роль «' + this.selectedDomainRole.title + '» недоступна для текущих основных ролей. '
            + this.allowedRolesText(this.selectedDomainRole);
        return;
      }
      if (this.needsScope && !this.selectedScopeIds.length) {
        this.errorText = 'Выберите области ответственности для доменной роли «'
            + (this.selectedDomainRole ? this.selectedDomainRole.title : '') + '»';
        return;
      }

      const req = await api.putJson(
          '/domain-roles/' + this.selectedDomainRoleKey, this.selectedScopeIds, { userId: this.id });
      if (!req.ok) {
        this.errorText = req.error || 'Не удалось назначить доменную роль';
        return;
      }
      this.selectedDomainRoleKey = null;
      this.selectedScopeIds = [];
      await this.loadDomainRoles();
    },

    async removeDomainRole(roleKey) {
      const req = await api.delete('/domain-roles/' + roleKey, {userId: this.id});
      if (!req.ok) {
        this.errorText = 'Не удалось снять доменную роль';
        return;
      }
      await this.loadDomainRoles();
    },

    async loadDepartments() {
      let req = await this.$getApi("/department/getList");
      if (req.ok) {
        this.departments = req.payload;
      } else {
        this.errorText =
            "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
    },

    async loadSecondaryRoleOptions() {
      let req = await this.$getApi("/getSecondaryRolesList");
      if (req.ok) {
        this.secondaryRoleOptions = req.payload;
      } else {
        this.errorText =
            "Ошибка подключения к серверу или обработки запроса: " + req.error;
      }
    }
  },
  async beforeMount() {
    let p1 = this.loadRegionsList();
    let p2 = this.loadDepartments();
    let p3 = this.loadSecondaryRoleOptions();
    this.id = this.$route.params.id;
    if (this.id != null && this.id !== "") {
      await this.loadUserInfo();
      await this.loadDomainRoles();
    }
    await p1;
    await p2;
    await p3;

    if (this.$user.role === 'ROLE_REGIONAL_ADMINISTRATOR') {
      this.roleOptions = this.roleOptions.filter(option => REGIONAL_ADMIN_MANAGED_ROLES.includes(option.type))
      this.user.region = this.$user.region
      this.departments = this.departments.filter(e=>e.region.code === this.$user.region.code)
    }

    this.previousPrimaryRole = this.user.primaryRole;
    this.onSelectChange();
    this.apiLoaded = true;
  },
};
</script>